import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Users, TrendingUp, MessageSquare, Heart, Search, UserPlus, Calendar } from 'lucide-react';

interface AnalyticsData {
  date: string;
  dau: number;
  totalSessions: number;
  avgSessionDuration: number;
  postCount: number;
  commentCount: number;
  likeCount: number;
  messageSentCount: number;
  searchCount: number;
  newMemberCount: number;
}

interface MonthlyData {
  monthEndDate: string;
  avgDau: number;
  totalMau: number;
  totalPosts: number;
  totalComments: number;
  totalLikes: number;
  totalMessages: number;
  engagementRate: number;
}

interface RetentionData {
  d1_retention: number;
  d7_retention: number;
  d30_retention: number;
}

interface Props {
  familyId: string;
}

const Analytics: React.FC<Props> = ({ familyId }) => {
  const [dailyMetrics, setDailyMetrics] = useState<AnalyticsData[]>([]);
  const [monthlyMetrics, setMonthlyMetrics] = useState<MonthlyData[]>([]);
  const [retentionData, setRetentionData] = useState<RetentionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchAnalyticsData();
  }, [familyId, timeRange]);

  const getDaysBack = () => {
    switch (timeRange) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 30;
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch daily metrics
      const daysBack = getDaysBack();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysBack);

      const dailyQuery = query(
        collection(db, 'analyticsMetrics'),
        where('familyId', '==', familyId),
        where('timestamp', '>=', startDate),
        where('period', '!=', 'monthly'),
        where('period', '!=', 'weekly'),
        where('period', '!=', 'retention'),
        orderBy('timestamp', 'desc'),
        limit(daysBack)
      );

      const dailySnapshot = await getDocs(dailyQuery);
      const dailyData = dailySnapshot.docs.map((doc) => ({
        date: doc.data().date,
        dau: doc.data().dau,
        totalSessions: doc.data().totalSessions,
        avgSessionDuration: Math.round(doc.data().avgSessionDuration / 60), // Convert to minutes
        postCount: doc.data().postCount,
        commentCount: doc.data().commentCount,
        likeCount: doc.data().likeCount,
        messageSentCount: doc.data().messageSentCount,
        searchCount: doc.data().searchCount,
        newMemberCount: doc.data().newMemberCount,
      })).reverse();

      setDailyMetrics(dailyData);

      // Fetch monthly metrics
      const monthlyQuery = query(
        collection(db, 'analyticsMetrics'),
        where('familyId', '==', familyId),
        where('period', '==', 'monthly'),
        orderBy('timestamp', 'desc'),
        limit(12)
      );

      const monthlySnapshot = await getDocs(monthlyQuery);
      const monthlyData = monthlySnapshot.docs.map((doc) => ({
        monthEndDate: doc.data().monthEndDate,
        avgDau: Math.round(doc.data().avgDau),
        totalMau: doc.data().totalMau,
        totalPosts: doc.data().totalPosts,
        totalComments: doc.data().totalComments,
        totalLikes: doc.data().totalLikes,
        totalMessages: doc.data().totalMessages,
        engagementRate: Math.round(doc.data().engagementRate * 100) / 100,
      })).reverse();

      setMonthlyMetrics(monthlyData);

      // Fetch retention data
      const retentionQuery = query(
        collection(db, 'analyticsMetrics'),
        where('familyId', '==', familyId),
        where('period', '==', 'retention'),
        orderBy('timestamp', 'desc'),
        limit(1)
      );

      const retentionSnapshot = await getDocs(retentionQuery);
      if (!retentionSnapshot.empty) {
        setRetentionData({
          d1_retention: Math.round(retentionSnapshot.docs[0].data().d1_retention),
          d7_retention: Math.round(retentionSnapshot.docs[0].data().d7_retention),
          d30_retention: Math.round(retentionSnapshot.docs[0].data().d30_retention),
        });
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const latestMetrics = dailyMetrics[dailyMetrics.length - 1];
  const previousMetrics = dailyMetrics[dailyMetrics.length - 2];

  const calculateChange = (current: number, previous: number) => {
    if (!previous || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Engagement breakdown pie chart
  const engagementData = latestMetrics
    ? [
        { name: 'Posts', value: latestMetrics.postCount },
        { name: 'Comments', value: latestMetrics.commentCount },
        { name: 'Likes', value: latestMetrics.likeCount },
        { name: 'Messages', value: latestMetrics.messageSentCount },
      ].filter((item) => item.value > 0)
    : [];

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <div className="flex gap-2">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics Cards */}
      {latestMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* DAU Card */}
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Daily Active Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{latestMetrics.dau}</p>
                {previousMetrics && (
                  <p
                    className={`text-sm font-medium mt-2 ${
                      calculateChange(latestMetrics.dau, previousMetrics.dau) >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {calculateChange(latestMetrics.dau, previousMetrics.dau) > 0 ? '+' : ''}
                    {calculateChange(latestMetrics.dau, previousMetrics.dau)}% from yesterday
                  </p>
                )}
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          {/* Sessions Card */}
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Sessions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{latestMetrics.totalSessions}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Avg. {latestMetrics.avgSessionDuration}m each
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>

          {/* Posts Card */}
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-600">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Posts Created</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{latestMetrics.postCount}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {latestMetrics.likeCount + latestMetrics.commentCount} interactions
                </p>
              </div>
              <MessageSquare className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          {/* Messages Card */}
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-600">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Messages Sent</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{latestMetrics.messageSentCount}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {latestMetrics.searchCount} searches performed
                </p>
              </div>
              <Heart className="w-8 h-8 text-orange-600" />
            </div>
          </div>
        </div>
      )}

      {/* Retention Metrics */}
      {retentionData && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Retention Rates</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm font-medium">Day 1 Retention</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{retentionData.d1_retention}%</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm font-medium">Day 7 Retention</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{retentionData.d7_retention}%</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm font-medium">Day 30 Retention</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">{retentionData.d30_retention}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DAU Trend */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Daily Active Users Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyMetrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                interval={Math.max(0, Math.floor(dailyMetrics.length / 7) - 1)}
              />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="dau"
                stroke="#3B82F6"
                dot={{ fill: '#3B82F6' }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Engagement Breakdown */}
        {engagementData.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Engagement Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={engagementData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {engagementData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Session Duration */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Average Session Duration</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyMetrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                interval={Math.max(0, Math.floor(dailyMetrics.length / 7) - 1)}
              />
              <YAxis />
              <Tooltip formatter={(value) => `${value}m`} />
              <Bar dataKey="avgSessionDuration" fill="#10B981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Content Metrics */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Content Creation Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyMetrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                interval={Math.max(0, Math.floor(dailyMetrics.length / 7) - 1)}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="postCount" fill="#3B82F6" name="Posts" />
              <Bar dataKey="commentCount" fill="#10B981" name="Comments" />
              <Bar dataKey="likeCount" fill="#F59E0B" name="Likes" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Summary */}
      {monthlyMetrics.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Monthly Summary</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Month</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Avg DAU</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">MAU</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Posts</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Comments</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Engagement Rate</th>
                </tr>
              </thead>
              <tbody>
                {monthlyMetrics.map((month, idx) => (
                  <tr key={idx} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{month.monthEndDate}</td>
                    <td className="px-4 py-3 text-gray-700">{month.avgDau}</td>
                    <td className="px-4 py-3 text-gray-700">{month.totalMau}</td>
                    <td className="px-4 py-3 text-gray-700">{month.totalPosts}</td>
                    <td className="px-4 py-3 text-gray-700">{month.totalComments}</td>
                    <td className="px-4 py-3 font-semibold text-blue-600">{month.engagementRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
